/**
 * client/src/components/notifications/notification-bell.tsx
 *
 * Icon button with unread-count badge and full ARIA wiring. AC-2, AC-3, AC-4, AC-12, AC-21.
 *
 * Uses React.forwardRef so Radix `Popover.Trigger asChild` can measure the
 * trigger element for positioning. Without forwarded ref the drawer renders
 * at (0, ~0) offscreen.
 */

import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Bell } from "lucide-react";

interface NotificationBellProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly total: number;
  readonly isOpen: boolean;
  readonly onToggle: () => void;
  readonly forensicPauseActive: boolean;
  readonly forensicPauseBannerId?: string;
  /**
   * True when the feed could not be fully loaded. The bell is the ONLY
   * always-visible surface, so without a marker here a failed feed renders a
   * plain badge-less icon — visually identical to "all clear" — and the user
   * has no reason to open the drawer where the honest notice lives.
   */
  readonly degraded?: boolean;
}

export const NotificationBell = forwardRef<HTMLButtonElement, NotificationBellProps>(
  function NotificationBell(
    {
      total,
      isOpen,
      onToggle,
      forensicPauseActive,
      forensicPauseBannerId,
      degraded = false,
      // Strip event handlers Radix `Popover.Trigger asChild` would otherwise
      // pass through; we drive toggling via our own onClick → onToggle so the
      // controlled Popover.Root state stays single-source-of-truth and avoids
      // double-toggle on each click.
      onClick: _radixOnClick,
      onPointerDown: _radixOnPointerDown,
      onKeyDown: _radixOnKeyDown,
      ...rest
    },
    ref,
  ) {
    const badgeText = total >= 10 ? "9+" : String(total);
    // Degraded wording wins: claiming "no unread" over a feed we failed to load
    // would be exactly the false assurance the degraded flag exists to prevent.
    const ariaLabel = degraded
      ? "Notifications, status unavailable"
      : total === 0
        ? "Notifications, no unread"
        : `Notifications, ${total} unread`;

    return (
      <button
        {...rest}
        ref={ref}
        type="button"
        onClick={onToggle}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-describedby={
          forensicPauseActive && forensicPauseBannerId ? forensicPauseBannerId : undefined
        }
        className="relative flex items-center justify-center w-9 h-9 rounded-full text-text-secondary hover:bg-bg-elevated transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={20} aria-hidden />

        {/*
          Degraded marker — NEUTRAL GREY, never amber. Amber means "you have
          something to act on" (CLAUDE.md §Design Constraints reserve it for
          user errors/dormancy); a load failure is not an actionable item, and
          colouring it amber would inflate the actionable signal. Rendered only
          when there is no count badge to show, so the two never collide.
        */}
        {degraded && total === 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-text-tertiary"
            aria-hidden
            data-testid="bell-degraded-dot"
          />
        )}

        {/* Badge — AC-2, AC-3 */}
        {total > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-warning text-bg-primary text-[10px] font-semibold flex items-center justify-center"
            aria-hidden
            data-testid="bell-badge"
          >
            {badgeText}
          </span>
        )}
      </button>
    );
  },
);
